import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate, spring, Easing } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";
import { DevaKathaShare } from "./DevaKathaShare";

const { fontFamily: noto } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });
const SAFFRON = "#FF9933";
const CREAM = "#FFF4E0";
const SHADOW = "0 4px 14px rgba(0,0,0,.9), 0 2px 5px rgba(0,0,0,.95)";

// ── Trimbak-style phrase treatment (shared) ─────────────────────────────
const YELLOW = "#FFD60A", WHITE = "#FFFFFF", RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const PAD = 25, BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32, LH = 1.18, LINE_GAP = -10;
const HOLES_MASK = {
  WebkitMaskImage: `url(${HOLES_DATA_URI})`, maskImage: `url(${HOLES_DATA_URI})`,
  WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
  WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
  WebkitMaskPosition: "0 0", maskPosition: "0 0",
} as const;
type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 1. TOP-LEFT phrase — Trimbak treatment: holes-mask distress + per-word colour/size +
//    word-by-word drop-from-top. Each cluster renders in its own window (animates from f=0).
//    Default = घुट-घुट कर / मर गईं? (मर गईं red). Modal passes `lines` per window.
export const RadhaPhraseTL: React.FC<{ lines?: Word[][] }> = ({
  lines = [[{ t: "घुट-घुट" }, { t: "कर" }], [{ t: "मर", c: "r", big: true }, { t: "गईं?", c: "r", big: true }]],
}) => {
  const f = useCurrentFrame(); const { fps } = useVideoConfig();
  const t = f / fps;
  let gi = -1;
  return (
    <AbsoluteFill style={{ backgroundColor: "transparent", ...HOLES_MASK }}>
      <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily: noto, display: "flex", flexDirection: "column" }}>
        {lines.map((line, li) => (
          <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : LINE_GAP }}>
            {line.map((w, wi) => {
              gi += 1;
              const aT = gi * STAGGER;
              const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
              const ease = 1 - Math.pow(1 - k, 3);
              const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              return (
                <span key={wi} style={{
                  display: "inline-block", transform: `translateY(${-DROP * (1 - ease)}px)`, opacity: op,
                  color: col(w.c), fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                  fontWeight: w.big ? 900 : 800, lineHeight: LH, letterSpacing: "0.005em", textShadow: SH,
                }}>{w.t}</span>
              );
            })}
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

// 2. BOTTOM-MIDDLE spoken-line quote — fade-up
export const RadhaQuoteBMA: React.FC<{ text?: string }> = ({ text = '"कान्हा"' }) => {
  const f = useCurrentFrame(); const { fps } = useVideoConfig();
  const op = interpolate(f, [0, 0.5 * fps], [0, 1], { extrapolateRight: "clamp" });
  const y = interpolate(f, [0, 0.6 * fps], [22, 0], { extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  return (
    <AbsoluteFill>
      <div style={{ position: "absolute", bottom: 72, left: 0, right: 0, textAlign: "center", opacity: op, transform: `translateY(${y}px)`, fontFamily: noto, fontWeight: 700, fontSize: 52, color: CREAM, textShadow: SHADOW }}>{text}</div>
    </AbsoluteFill>
  );
};

// 3. CENTER comment card — "Comment" small above, big "राधे राधे" below, soft pulse
export const RadhaComment: React.FC = () => {
  const f = useCurrentFrame(); const { fps } = useVideoConfig();
  const s = spring({ frame: f, fps, config: { damping: 13, stiffness: 90 } });
  const sc = interpolate(s, [0, 1], [0.7, 1]);
  const op = interpolate(f, [0, 0.4 * fps], [0, 1], { extrapolateRight: "clamp" });
  const pulse = 1 + 0.03 * Math.sin((f / fps) * 4);
  // soft BLURRED shadow (drop-shadow blurs the glyph shape itself — a real shadow,
  // not an outline/boundary). Stacked radii = soft dark halo, no box.
  const SOFT = "drop-shadow(0 4px 10px rgba(0,0,0,.85)) drop-shadow(0 0 22px rgba(0,0,0,.75)) drop-shadow(0 0 44px rgba(0,0,0,.55))";
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center" }}>
      <div style={{ opacity: op, transform: `scale(${sc * pulse})`, filter: SOFT, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div style={{ fontFamily: noto, fontWeight: 700, fontSize: 32, color: "#fff", letterSpacing: 2, opacity: 0.9 }}>Comment</div>
        <div style={{ fontFamily: noto, fontWeight: 800, fontSize: 128, color: SAFFRON }}>राधे राधे</div>
      </div>
    </AbsoluteFill>
  );
};

// 4. BOTTOM-LEFT "आगे देखिए" with 3 blinking/cycling dots
export const RadhaAgeDekhiye: React.FC = () => {
  const f = useCurrentFrame(); const { fps } = useVideoConfig();
  const n = Math.floor(((f / fps) * 1.5) % 4); // 0,1,2,3 → "", ".", "..", "..."
  return (
    <AbsoluteFill>
      <div style={{ position: "absolute", bottom: 46, left: 46, fontFamily: noto, fontWeight: 700, fontSize: 46, color: CREAM, textShadow: SHADOW }}>
        आगे देखिए<span style={{ color: SAFFRON }}>{".".repeat(n)}</span>
      </div>
    </AbsoluteFill>
  );
};

// ── MASTER composition — every text overlay placed at its merged-timeline second.
//    16fps, 1280×704, full video length. Modal fans this out by frame window and
//    composites onto the clean base (radha_full_v2.mp4). Each comp handles its own
//    intro; Clip adds a 0.4s tail fade-out so nothing pops off abruptly.
const Clip: React.FC<{ durF: number; children: React.ReactNode }> = ({ durF, children }) => {
  const f = useCurrentFrame(); const { fps } = useVideoConfig();
  const out = interpolate(f, [durF - 0.4 * fps, durF], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: out }}>{children}</AbsoluteFill>;
};

export const RadhaOverlaysFull: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  const seq = (k: string, fromS: number, durS: number, node: React.ReactNode) => (
    <Sequence key={k} from={S(fromS)} durationInFrames={S(durS)}><Clip durF={S(durS)}>{node}</Clip></Sequence>
  );
  return (
    <AbsoluteFill>
      {/* TOP-LEFT phrases (Trimbak holes treatment, yellow/white/red) — frame-locked to word_timestamps */}
      {seq("p1", 5.6, 3.6, <RadhaPhraseTL lines={[[{ t: "घुट-घुट" }, { t: "कर" }], [{ t: "मर", c: "r", big: true }, { t: "गईं?", c: "r", big: true }]]} />)}
      {seq("p2", 22.0, 3.0, <RadhaPhraseTL lines={[[{ t: "खौफनाक", c: "r", big: true }], [{ t: "अंत?" }]]} />)}
      {seq("p3", 25.7, 3.0, <RadhaPhraseTL lines={[[{ t: "दर्दनाक", c: "r", big: true }], [{ t: "मौत?", c: "w", big: true }]]} />)}
      {seq("p4", 88.7, 3.0, <RadhaPhraseTL lines={[[{ t: "स्मारक", c: "w", big: true }], [{ t: "क्यों" }, { t: "नहीं?" }]]} />)}
      {/* BOTTOM-MIDDLE spoken-line quotes (devotional, clean) */}
      {seq("q1", 158.3, 3.6, <RadhaQuoteBMA text={'"मेरे जाने का समय आ गया"'} />)}
      {seq("q2", 199.0, 2.8, <RadhaQuoteBMA text={'"कान्हा"'} />)}
      {seq("q3", 235.0, 2.8, <RadhaQuoteBMA text={'"राधे…"'} />)}
      {/* CENTER comment cards (dark backdrop so they don't submerge) */}
      {seq("cm1", 50.2, 3.4, <RadhaComment />)}
      {seq("cm2", 394.4, 3.9, <RadhaComment />)}
      {/* BOTTOM-LEFT teaser — over the inserted opener */}
      {seq("dk", 52.5, 14.5, <RadhaAgeDekhiye />)}
      {/* CTA pills — fps-aware, self-fading over ~4s. No Clip wrapper (own fade-out). */}
      <Sequence from={S(46.3)} durationInFrames={S(4)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(357.8)} durationInFrames={S(4)}><DevaKathaLS5 /></Sequence>
      <Sequence from={S(388.2)} durationInFrames={S(4)}><DevaKathaShare /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
